"use client";

import { ReactNode, useEffect, useState } from 'react';
import i18n from './i18n';
import { I18nextProvider } from 'react-i18next';
import { detectLanguageFromLocation } from '@/lib/geolocation';
import { safeStorage, I18N_STORAGE_KEY } from '@/lib/safe-storage';

interface I18nProviderProps {
  children: ReactNode;
}

export function I18nProvider({ children }: I18nProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    async function initializeLanguage() {
      try {
        // i18n is initialized in `i18n.ts`; avoid calling `init()` here again.
        // Re-initialization can recurse through plugins and cause call stack issues.
        if (!i18n.isInitialized) {
          await new Promise<void>((resolve) => {
            const timeoutId = setTimeout(() => {
              i18n.off('initialized', onInitialized);
              resolve();
            }, 1500);
            const onInitialized = () => {
              clearTimeout(timeoutId);
              i18n.off('initialized', onInitialized);
              resolve();
            };
            i18n.on('initialized', onInitialized);
          });
        }

        // Check if user has already selected a language manually
        const storedLang = safeStorage.getItem(I18N_STORAGE_KEY);
        const supportedLangs = Object.keys(i18n.options.resources || {});

        if (storedLang && supportedLangs.includes(storedLang)) {
          console.log('i18n: Using stored language:', storedLang);
          if (i18n.language !== storedLang) {
            await i18n.changeLanguage(storedLang);
          }
          setIsInitialized(true);
          return;
        }

        // Detect language from geolocation if not manually set
        const detectedLang = await detectLanguageFromLocation();
        console.log('i18n: Detected language from location:', detectedLang);

        if (i18n.language !== detectedLang) {
          await i18n.changeLanguage(detectedLang);
          // Note: i18n.changeLanguage() automatically triggers SafeLanguageDetector.cacheUserLanguage()
        }

        setIsInitialized(true);
      } catch (error) {
        console.error('Error initializing language:', error);
        // Fallback to English on error
        if (i18n.language !== 'en') {
          await i18n.changeLanguage('en');
        }
        setIsInitialized(true);
      }
    }

    initializeLanguage();
  }, []);

  // Don't render children until language is initialized to avoid flashing wrong language
  if (!isInitialized) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontSize: '14px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <I18nextProvider i18n={i18n}>
      {children}
    </I18nextProvider>
  );
} 
